import React, { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import App from './App.jsx'

// Stylish fallback component for errors
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div role="alert" className="error-boundary fixed inset-0 flex items-center justify-center bg-gradient-to-br from-neutral-950 to-neutral-900 z-50">
      <div className="relative backdrop-blur-xl p-8 rounded-2xl max-w-md w-full border border-neutral-800/50 shadow-2xl bg-neutral-900/30 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-gradient-to-br from-red-500/20 to-purple-500/20 blur-2xl"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 blur-2xl"></div>
        
        <div className="flex items-center mb-6">
          <div className="bg-red-500/10 p-3 rounded-full mr-4 border border-red-500/20">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-rose-600">Something went wrong</h2>
        </div>
        
        <div className="bg-neutral-950/60 p-4 rounded-xl mb-6 overflow-auto max-h-48 border border-neutral-800/50">
          <pre className="text-rose-400 text-sm font-mono">{error.message}</pre>
        </div>
        
        <button 
          onClick={resetErrorBoundary}
          className="w-full py-3 px-4 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white font-medium rounded-xl transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-red-500/20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          Try Again
        </button>
      </div>
    </div>
  );
};

// Create root and render app with error boundary
const root = createRoot(document.getElementById('root'));
root.render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.reload()}>
      <Suspense fallback={
        <div className="flex h-screen w-screen items-center justify-center bg-gradient-to-br from-neutral-950 to-neutral-900">
          <div className="flex flex-col items-center relative">
            {/* Background glow effects */}
            <div className="absolute w-64 h-64 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-full blur-3xl opacity-60"></div>
            
            {/* Spinner with multiple layers */}
            <div className="relative w-28 h-28">
              <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-500/80 border-t-transparent rounded-full animate-spin" style={{ animationDuration: '1.2s' }}></div>
              <div className="absolute top-3 left-3 right-3 bottom-3 border-4 border-accent-500/80 border-b-transparent rounded-full animate-spin" style={{ animationDuration: '1.8s', animationDirection: 'reverse' }}></div>
              <div className="absolute top-6 left-6 right-6 bottom-6 border-4 border-white/20 border-l-transparent border-r-transparent rounded-full animate-spin" style={{ animationDuration: '2.4s' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-primary-400 to-accent-400 animate-pulse">MS</span>
              </div>
            </div>
            
            <p className="mt-8 text-neutral-400 font-medium relative z-10">
              <span className="inline-block animate-pulse">Loading</span> 
              <span className="inline-block">amazing experience</span>
              <span className="inline-block animate-bounce">...</span>
            </p>
          </div>
        </div>
      }>
        <App />
      </Suspense>
    </ErrorBoundary>
  </StrictMode>,
);
